import React, {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useReducer,
  useState,
} from "react";
import { IAuthState, AuthAction, IUser, IOrganization } from "../types/auth";
import { AppRoutes, LocalStorageItems } from "../constants";
import { useNavigate } from "react-router-dom";
import { getMeUser } from "../Services_V2/Auth.service";
import { PageLoader } from "../components";
import { authReducer } from "./reducers/auth.reducer";
import { LOGOUT, SET_USER } from "../types/actions";

// ------------------ TYPES ------------------

export type AuthState = {
  user: IUser | null;
  userOrganization: IOrganization | null;
  isInitialized: boolean;
  isLoggedIn: boolean;
};

// ------------------ INITIAL STATE ------------------

export const authInitialState: AuthState = {
  user: null,
  userOrganization: null,
  isInitialized: false,
  isLoggedIn: false,
};

// ------------------ CONTEXT ------------------

interface AuthContextValue {
  state: AuthState;
  dispatch: React.Dispatch<AuthAction>;
  userLogout: () => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

// ------------------ PROVIDER ------------------

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(authReducer, authInitialState);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const initialize = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem(LocalStorageItems.TOKEN);

      if (token) {
        const data = await getMeUser();
        dispatch({
          type: SET_USER,
          payload: { user: data.user, userOrganization: data.entityDetails },
        });
      } else {
        dispatch({ type: LOGOUT });
      }
    } catch (error: any) {
      console.error("Auth initialization failed:", error);
      localStorage.removeItem(LocalStorageItems.TOKEN);
      dispatch({ type: "LOGOUT" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  const userLogout = useCallback(() => {
    localStorage.removeItem(LocalStorageItems.TOKEN);
    dispatch({ type: "LOGOUT" });
    navigate(AppRoutes.ORGANIZATION.LOGIN, { replace: true });
  }, [navigate]);

  if (!state.isInitialized || loading) {
    return <PageLoader />;
  }

  return (
    <AuthContext.Provider
      value={{
        state,
        dispatch,
        userLogout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
